import { createColumnHelper } from '@tanstack/react-table';
import { Table, Typography, useSnackbar, Prompt } from '@weareopenr/component-library';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styles from './PricingTab.module.scss';
import { IAggregator } from '../../types/aggregator.types';
import { IPriceBandWithVenues } from '../../types/priceBand.types';
import { IMenuItem } from '../../types/menu.types';
import { PriceInput } from '../PricingTablePriceInput/PricingTablePriceInput';
import { PricingSaveSummary } from '../PricingSaveSummary/PricingSaveSummary';
import { DELIMITER, generatePriceFormDefaults, submitPrices } from 'src/helpers/priceTable';
import { createPortal } from 'react-dom';
import { downStreamExternalSystemIntegrationTypes, PAGE_FOOTER_ID, PRICING_FORM_ID } from 'src/constants';
import { useRules } from '../../hooks/useRules/useRules';
import { IPriceRule } from '../../types/price.types';
import { useItem } from 'src/hooks/useItem/useItem';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { usePriceBands } from 'src/hooks/usePriceBands';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import SkeletonContainer from '../SkeletonContainer';
import PriceSchemeSelector from '../PriceSchemeSelector';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { useCancelCheck } from 'src/hooks/useCancelCheck';

const columnHelper = createColumnHelper<PricingTableRow>();

export type PricingTableRow = {
  priceBandId: string | null;
  priceBandName: string;
};

export interface PricingTabContentProps {
  downstreamExternalSystems: IAggregator[];
  priceBands: IPriceBandWithVenues[];
  item: IMenuItem;
  priceRules: IPriceRule[];
  priceSchemeId: string;
  onTotalChangesUpdate: (totalChanges: number) => void;
}

const Content = ({
  downstreamExternalSystems,
  priceBands,
  item,
  priceRules,
  priceSchemeId,
  onTotalChangesUpdate,
}: PricingTabContentProps) => {
  const { showSnackbar } = useSnackbar();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.item] });
  };

  const tableData: PricingTableRow[] = [
    { priceBandId: null, priceBandName: 'Default' },
    ...priceBands
      .filter((band) => band.priceBandId !== null)
      .map(({ name, priceBandId }) => ({
        priceBandId: priceBandId,
        priceBandName: name,
      })),
  ];

  const products = useMemo(() => {
    return [
      {
        item_id: item.itemId,
        prices: item.new_prices,
      },
    ];
  }, [item]);

  // Default values can only be set on initial render, so we can safely ignore the dependencies here
  const defaultValues = useMemo(() => generatePriceFormDefaults(products, priceBands, downstreamExternalSystems), []);

  const methods = useForm<Record<string, string>>({
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    formState: { dirtyFields, isSubmitting },
    reset,
    control,
  } = methods;

  const columns = useMemo(
    () => [
      columnHelper.accessor('priceBandName', {
        size: 150,
        header: 'Price Band',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('priceBandId', {
        header: 'Default Price',
        id: 'defaultPrice',
        size: 150,
        cell: (info) => {
          const inputName = `${item.itemId}${DELIMITER}null${DELIMITER}${info.getValue()}`;
          return (
            <PriceInput
              control={control}
              inputName={inputName}
              register={register}
              priceRules={priceRules}
              tableContainerRef={tableContainerRef}
            />
          );
        },
      }),
      ...downstreamExternalSystems.map((aggregator) => {
        return columnHelper.accessor('priceBandId', {
          id: aggregator.aggregatorId,
          header: aggregator.name,
          size: 150,
          cell: (info) => {
            const inputName = `${item.itemId}${DELIMITER}${aggregator.aggregatorId}${DELIMITER}${info.getValue()}`;
            return (
              <PriceInput
                control={control}
                inputName={inputName}
                register={register}
                priceRules={priceRules}
                tableContainerRef={tableContainerRef}
              />
            );
          },
        });
      }),
    ],
    [downstreamExternalSystems, item, priceRules],
  );

  const onSubmit = submitPrices(dirtyFields, products, reset, showSnackbar, invalidate, priceSchemeId);
  const totalChanges = Object.keys(dirtyFields).length;

  // Notify parent of changes count
  useEffect(() => {
    onTotalChangesUpdate(totalChanges);
  }, [totalChanges, onTotalChangesUpdate]);

  return (
    <form id={PRICING_FORM_ID} className={styles['form-container']} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles['table-container']}>
        <Table
          className={styles['table-fill-height']}
          data={tableData}
          columns={columns}
          columnsToPin={['priceBandName', 'defaultPrice']}
          tableContainerRef={tableContainerRef}
          EmptyState={() => (
            <div className={styles['no-results']}>
              <Typography>No prices found &nbsp;</Typography>
            </div>
          )}
        />
      </div>

      {totalChanges > 0 &&
        createPortal(
          <PricingSaveSummary totalChanges={totalChanges} reset={reset} isSubmitting={isSubmitting} />,
          document.getElementById(PAGE_FOOTER_ID),
        )}
    </form>
  );
};

const PricingDataLoader = ({
  itemId,
  priceSchemeId,
  onTotalChangesUpdate,
}: {
  itemId: string;
  priceSchemeId: string;
  onTotalChangesUpdate: (totalChanges: number) => void;
}) => {
  const { data: menuItem, isLoading: isItemLoading } = useItem(itemId, priceSchemeId);
  const { data: priceRules, isLoading: isPriceRulesLoading } = useRules(priceSchemeId);
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const { priceBands, isLoading: isPriceBandsLoading, isFetching: isPriceBandsFetching } = usePriceBands(priceSchemeId);

  const downstreamExternalSystems = useMemo(() => {
    return (
      externalSystems?.filter((aggregator) =>
        downStreamExternalSystemIntegrationTypes.includes(aggregator.integrationType),
      ) || []
    );
  }, [externalSystems]);

  const isLoadingPricingData =
    isExternalSystemsLoading || isPriceBandsLoading || isPriceBandsFetching || isPriceRulesLoading || isItemLoading;

  if (isLoadingPricingData) {
    return <SkeletonContainer hiddenTitle={true} />;
  }

  return (
    <Content
      downstreamExternalSystems={downstreamExternalSystems}
      priceBands={priceBands}
      item={menuItem}
      priceRules={priceRules}
      priceSchemeId={priceSchemeId}
      onTotalChangesUpdate={onTotalChangesUpdate}
    />
  );
};
export const PricingTab = ({ itemId }: { itemId: string }) => {
  const { selectedPriceSchemeId, setSelectedPriceSchemeId } = useSelectedPriceScheme();
  const [totalChanges, setTotalChanges] = useState(0);
  const [nextPriceSchemeId, setNextPriceSchemeId] = useState<string | null>(null);

  const { isCancelCheck, message, handleStay, handleLeave } = useCancelCheck({ totalChanges });

  const handlePriceSchemeChange = (newSchemeId: string) => {
    if (totalChanges > 0) {
      setNextPriceSchemeId(newSchemeId);
    } else {
      setSelectedPriceSchemeId(newSchemeId);
    }
  };

  const handleConfirmSchemeChange = () => {
    if (nextPriceSchemeId) {
      setSelectedPriceSchemeId(nextPriceSchemeId);
      setNextPriceSchemeId(null);
    }
  };

  const handleCancelSchemeChange = () => {
    setNextPriceSchemeId(null);
  };

  const isSchemeChangeCheck = nextPriceSchemeId !== null;
  const isPromptOpen = isCancelCheck || isSchemeChangeCheck;
  const promptButtonHandler = isCancelCheck ? handleLeave : handleConfirmSchemeChange;
  const promptCloseHandler = isCancelCheck ? handleStay : handleCancelSchemeChange;

  if (!selectedPriceSchemeId) {
    return <SkeletonContainer hiddenTitle={true} />;
  }

  return (
    <div className={styles['container']}>
      <Prompt
        closePrompt={promptCloseHandler}
        open={isPromptOpen}
        buttonHandler={promptButtonHandler}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {message}
      </Prompt>
      <PriceSchemeSelector value={selectedPriceSchemeId} onChange={handlePriceSchemeChange} />
      <PricingDataLoader itemId={itemId} priceSchemeId={selectedPriceSchemeId} onTotalChangesUpdate={setTotalChanges} />
    </div>
  );
};

export default PricingTab;
